// src/pages/AuthPage.jsx
// ─────────────────────────────────────────────────────────────
//  로그인 / 회원가입 화면 (이메일 + Google)
// ─────────────────────────────────────────────────────────────

import { useState } from "react";
import { signIn, signUp, signInWithGoogle, resetPassword } from "../services/authService";

export default function AuthPage({ onSuccess }) {
  const [mode,   setMode]   = useState("login");   // login | signup | reset
  const [form,   setForm]   = useState({ email: "", password: "", name: "", phone: "", role: "worker" });
  const [loading, setLoading] = useState(false);
  const [msg,    setMsg]    = useState(null);       // { type: ok|error, text }

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  async function handleSubmit() {
    setLoading(true); setMsg(null);
    try {
      if (mode === "login") {
        await signIn(form.email, form.password);
      } else if (mode === "signup") {
        if (!form.name.trim()) { setMsg({ type: "error", text: "이름을 입력하세요" }); return; }
        await signUp(form.email, form.password, { name: form.name, phone: form.phone, role: form.role });
      } else {
        await resetPassword(form.email);
        setMsg({ type: "ok", text: "비밀번호 재설정 이메일을 발송했습니다." });
        return;
      }
      onSuccess?.();
    } catch (e) {
      const msgs = {
        "auth/user-not-found":       "등록되지 않은 이메일입니다.",
        "auth/wrong-password":       "비밀번호가 틀렸습니다.",
        "auth/email-already-in-use": "이미 사용 중인 이메일입니다.",
        "auth/weak-password":        "비밀번호는 6자 이상이어야 합니다.",
        "auth/invalid-email":        "올바른 이메일 형식이 아닙니다.",
      };
      setMsg({ type: "error", text: msgs[e.code] ?? e.message });
    } finally {
      setLoading(false);
    }
  }

  async function handleGoogle() {
    setLoading(true);
    try { await signInWithGoogle(); onSuccess?.(); }
    catch (e) { setMsg({ type: "error", text: e.message }); }
    finally { setLoading(false); }
  }

  const inputStyle = {
    width: "100%", boxSizing: "border-box",
    border: "2px solid #eee", borderRadius: 12,
    padding: "13px 16px", fontSize: 15, fontFamily: "inherit",
    outline: "none", marginBottom: 12, background: "#f9f9f9",
  };

  return (
    <div style={{ minHeight: "100vh", background: "linear-gradient(160deg, #1a1a2e 0%, #0f3460 60%, #16213e 100%)", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: 24 }}>

      {/* 로고 */}
      <div style={{ marginBottom: 36, textAlign: "center" }}>
        <div style={{ fontSize: 48 }}>💼</div>
        <div style={{ color: "#FFD60A", fontSize: 26, fontWeight: 900, fontFamily: "Georgia, serif", marginTop: 8 }}>하루일거리</div>
        <div style={{ color: "rgba(255,255,255,0.6)", fontSize: 13, marginTop: 4 }}>오늘 할 일, 오늘 구하세요</div>
      </div>

      {/* 카드 */}
      <div style={{ background: "#fff", borderRadius: 24, padding: 28, width: "100%", maxWidth: 380 }}>
        <div style={{ fontSize: 20, fontWeight: 900, marginBottom: 24, color: "#1a1a2e" }}>
          {mode === "login" ? "로그인" : mode === "signup" ? "회원가입" : "비밀번호 재설정"}
        </div>

        {/* 알림 */}
        {msg && (
          <div style={{ background: msg.type === "ok" ? "#e8f5e9" : "#ffebee", borderRadius: 10, padding: "10px 14px", marginBottom: 16, fontSize: 13, color: msg.type === "ok" ? "#2e7d32" : "#c62828", fontWeight: 600 }}>
            {msg.type === "ok" ? "✅ " : "⚠️ "}{msg.text}
          </div>
        )}

        {/* 이름 (회원가입만) */}
        {mode === "signup" && (
          <>
            <input value={form.name} onChange={e => set("name", e.target.value)}
              placeholder="이름" style={inputStyle} />
            <input value={form.phone} onChange={e => set("phone", e.target.value)}
              placeholder="연락처 (선택)" style={inputStyle} type="tel" />
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: "#555", marginBottom: 8 }}>가입 유형</div>
              <div style={{ display: "flex", gap: 10 }}>
                {[["worker", "👷 구직자"], ["employer", "🏢 업체"]].map(([v, label]) => (
                  <button key={v} onClick={() => set("role", v)} style={{
                    flex: 1, padding: "10px", borderRadius: 12,
                    border: form.role === v ? "none" : "2px solid #eee",
                    background: form.role === v ? "#1a1a2e" : "#f8f8f8",
                    color: form.role === v ? "#FFD60A" : "#555",
                    fontWeight: 700, fontSize: 14, cursor: "pointer",
                  }}>{label}</button>
                ))}
              </div>
            </div>
          </>
        )}

        <input value={form.email} onChange={e => set("email", e.target.value)}
          placeholder="이메일" type="email" style={inputStyle} />

        {mode !== "reset" && (
          <input value={form.password} onChange={e => set("password", e.target.value)}
            placeholder="비밀번호 (6자 이상)" type="password" style={inputStyle} />
        )}

        {/* 주요 버튼 */}
        <button onClick={handleSubmit} disabled={loading} style={{
          width: "100%", background: loading ? "#aaa" : "#1a1a2e",
          color: "#FFD60A", border: "none", borderRadius: 14,
          padding: "15px", fontSize: 16, fontWeight: 900, cursor: loading ? "not-allowed" : "pointer",
          marginBottom: 14,
        }}>
          {loading ? "처리 중..." : mode === "login" ? "로그인" : mode === "signup" ? "가입하기" : "재설정 이메일 발송"}
        </button>

        {/* Google 로그인 */}
        {mode !== "reset" && (
          <button onClick={handleGoogle} disabled={loading} style={{
            width: "100%", background: "#fff", border: "2px solid #eee",
            borderRadius: 14, padding: "13px", fontSize: 14, fontWeight: 700,
            cursor: "pointer", marginBottom: 20, display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
          }}>
            <span style={{ fontSize: 18 }}>🔵</span> Google로 계속하기
          </button>
        )}

        {/* 전환 링크 */}
        <div style={{ textAlign: "center", fontSize: 13, color: "#888" }}>
          {mode === "login" ? (
            <>
              계정이 없으신가요?{" "}
              <span onClick={() => { setMode("signup"); setMsg(null); }} style={{ color: "#1a1a2e", fontWeight: 700, cursor: "pointer" }}>회원가입</span>
              {" · "}
              <span onClick={() => { setMode("reset"); setMsg(null); }} style={{ color: "#888", cursor: "pointer" }}>비밀번호 찾기</span>
            </>
          ) : (
            <span onClick={() => { setMode("login"); setMsg(null); }} style={{ color: "#1a1a2e", fontWeight: 700, cursor: "pointer" }}>
              ← 로그인으로
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
